import React, { useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BlogContext } from '../BlogContext';
import AdminHeader from '../components/AdminHeader';
import { useAuth } from '../AuthContext';

const AdminBlogStats = () => {
  const { posts, loading } = useContext(BlogContext);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString + "T00:00:00").toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Sort posts by views (highest first)
  const sortedPosts = posts.slice().sort((a, b) => (Number(b.views) || 0) - (Number(a.views) || 0));
  const totalViews = posts.reduce((sum, post) => sum + (Number(post.views) || 0), 0);

  // Debug logging
  console.log('Posts:', posts);
  console.log('Total Views Calculation:', totalViews);
  console.log('Posts Length:', posts.length);
  console.log('Average Calculation:', posts.length > 0 ? totalViews / posts.length : 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] text-white">
        <AdminHeader />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-4">Loading blog statistics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white admin-page">
      <AdminHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Blog Statistics</h1>
          <Link
            to="/admin/blog"
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-sm font-medium transition-colors"
          >
            Create New Post
          </Link>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-600 rounded-lg">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 002 0V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-400">Total Posts</p>
                <p className="text-2xl font-bold">{posts.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-600 rounded-lg">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-400">Total Views</p>
                <p className="text-2xl font-bold">{totalViews}</p>
              </div>
            </div>
          </div>

          <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-600 rounded-lg">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-400">Avg Views/Post</p>
                <p className="text-2xl font-bold">
                  {posts.length > 0 ? Math.round(totalViews / posts.length) : 0}
                </p>
                <p className="text-xs text-gray-500">
                  ({totalViews} views ÷ {posts.length} posts)
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Blog Posts Table */}
        <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-[#4d4d4d]">
            <h2 className="text-xl font-bold">All Blog Posts</h2>
          </div>
          
          {posts.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <p>No blog posts found.</p>
              <Link
                to="/admin/blog"
                className="text-blue-500 hover:text-blue-400 mt-2 inline-block"
              >
                Create your first post
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#2a2a2a]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Views
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Published
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Last Viewed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#4d4d4d]">
                  {sortedPosts.map((post) => (
                    <tr key={post.id} className="hover:bg-[#2a2a2a] transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-white">
                              {post.title}
                            </div>
                            <div className="text-sm text-gray-400">
                              By {post.author} • {post.timeToRead}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white font-medium">
                          {post.views || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-300">
                          {formatDate(post.publishedDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-300">
                          {formatDateTime(post.lastViewed)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <Link
                          to={`/blog/${post.id}`}
                          target="_blank"
                          className="text-blue-500 hover:text-blue-400"
                        >
                          View
                        </Link>
                        <span className="text-gray-500">•</span>
                        <Link
                          to={`/admin/blog-analytics/${post.id}`}
                          className="text-purple-500 hover:text-purple-400"
                        >
                          Analytics
                        </Link>
                        <span className="text-gray-500">•</span>
                        <button
                          onClick={() => {
                            // Navigate to blog editor with this post's data
                            navigate('/admin/blog', { state: { editPost: post } });
                          }}
                          className="text-green-500 hover:text-green-400"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBlogStats; 